class ProductGrouping extends HTMLElement {
  constructor() {
    super();
    this.debug = false; // Set to true for debugging
    this.parentComponent = this;
    this.sectionId = this.getAttribute("data-section-id");
    this.optionsToRequestData = this.querySelectorAll(
      "[js-request-new-product]"
    );
    this.buttonsToUpdateSubOptions = this.querySelectorAll(
      "[js-update-sub-option]"
    );
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");

    this.defaultVariantId = this.getAttribute("data-default-variant-id");
    this.currentProductId = this.getAttribute("data-current-product-id");
    this.currentVariantId = this._getVariantFromURL() || this.defaultVariantId;
    this.currentVariantAvailable =
      this.getAttribute("data-default-variant-available") === "true";
    this.currentProductHandle = this.getAttribute(
      "data-current-product-handle"
    );
    this.currentOptions = {
      option2: this.getAttribute("data-current-option2"),
      option3: this.getAttribute("data-current-option3"),
    };

    // Track user's preferred size choice throughout the PDP session
    this.preferredSize = null;
    // If variant is in URL, treat it as user-selected
    const urlParams = new URLSearchParams(window.location.search);
    this.hasUserSelectedSize = urlParams.has("variant");

    // Track current variant availability for AMP modal close protection
    this.currentRealTimeAvailable = null;

    // Performance: Cache product data to avoid redundant fetches
    this.productCache = new Map();
    this.htmlCache = new Map();

    this.isQuickView =
      this.dataset.isQuickView === "true" ||
      Boolean(this.closest("quick-view-modal"));

    if (this.isQuickView) {
      if (this.debug) console.log("ProductGrouping: quick-view mode enabled");
    }

    this.productGroupingData = {};
    this.dataProductGroup = JSON.parse(
      this.querySelector("script[data-product-group]")?.textContent || "{}"
    );

    // Prevent custom.js from interfering with our button state management
    this._preventVariantChangeInterference();

    this._checkAndSetDefaultVariant();
    this._bindEvents();
    this._setupSizeGuide();

    // CRITICAL: Set up button state guardian to prevent buttons from disappearing
    this._setupButtonStateGuardian();
  }

  _preventVariantChangeInterference() {
    // NOTE: We should NOT block variant:change events - other systems need them
    // The ProductGrouping component will dispatch its own variant:change events
    // and manage button state directly, ensuring consistency
  }

  _bindEvents() {
    this.optionsToRequestData.forEach((option) => {
      option.addEventListener("click", (event) =>
        this._handleOptionChange(event, option)
      );
    });
    this._bindVariantButtons();
  } 

  _bindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      // Store the bound handler for later removal
      const handler = (event) => this._handleUpdateVariant(event, button);
      button._variantClickHandler = handler;
      button.addEventListener('click', handler);
    });
  }

  _rebindVariantButtons() {
    this.buttonsToUpdateVariant.forEach(button => {
      if (button._variantClickHandler) {
        button.removeEventListener('click', button._variantClickHandler);
        button._variantClickHandler = null;
      }
    });
    this.buttonsToUpdateVariant = this.querySelectorAll("[js-update-variant]");
    this._bindVariantButtons();
  }

  _checkAndSetDefaultVariant() {
    if (!this.currentVariantId && this.defaultVariantId) {
      this._updateURLWithVariant(this.defaultVariantId);
      this.currentVariantId = this.defaultVariantId;
    }

    const scriptTagsData = this.querySelectorAll(
      "script[js-product-group-data]"
    );
    this.productGroupingData = Array.from(scriptTagsData).reduce(
      (acc, script) => {
        const data = JSON.parse(script.textContent || "{}");
        const key = script.getAttribute("data-product-id");
        if (!acc[key]) {
          acc[key] = { tag_options: [], variants: [] };
        }
        const existingData = acc[key];
        existingData.tag_options.push(...data.tag_options);
        const uniqueVariants = data.variants.filter(
          (newVariant) =>
            !existingData.variants.some(
              (existingVariant) => existingVariant.id === newVariant.id
            )
        );
        existingData.variants.push(...uniqueVariants);
        return acc;
      },
      {}
    );

    // Check real-time availability for the current variant on page load
    if (this.currentVariantId) {
      this._fetchVariantAvailability(this.currentVariantId)
        .then((realTimeAvailable) => {
          const addToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const addToCartButton = addToCartForm?.querySelector(
            'buy-buttons button[type="submit"]'
          );
          const notifyMeBtn = addToCartForm?.querySelector(
            "buy-buttons button[data-bis-trigger]"
          );
          const addToCartButtonText = addToCartButton?.querySelector("div");
          const soldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          if (addToCartButton && notifyMeBtn && addToCartButtonText) {
            if (!realTimeAvailable) {
              // If user hasn't selected a size, try to find first available
              if (!this.hasUserSelectedSize) {
                this._findFirstAvailableVariantAsync().then(
                  (firstAvailableVariant) => {
                    if (firstAvailableVariant) {
                      this._switchToVariant(firstAvailableVariant);
                    } else {
                      this._updateButtonVisibility(
                        addToCartButton,
                        notifyMeBtn,
                        addToCartButtonText,
                        soldOutElement,
                        false
                      );
                      this._setupWaitlistScroll();
                    }
                  }
                );
                return;
              }

              // User selected a specific unavailable size
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                false
              );
              this._setupWaitlistScroll();
            } else {
              // Variant is available - ensure button is visible
              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                addToCartButtonText,
                soldOutElement,
                true
              );
            }
          }
        })
        .catch((error) => {
          console.error(
            "[ProductGrouping] Failed to fetch initial variant availability:",
            error
          );
        });
    }

    //Force update the stock message on load
    const stockMessageEl = document.querySelector(
      'product-stock-message[data-context="pdp"]'
    );
    if (stockMessageEl && this.currentVariantId) {
      //console.log('[ProductGrouping] Initializing stock message for PDP with variant:', this.currentVariantId);
      stockMessageEl.updateVariantId(this.currentVariantId);
    }
  }
 
  _getVariantFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  }

  _updateURLWithVariant(variantId) {
    if (this.isQuickView || !variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  _handleUpdateVariant(event, button) {
    event.preventDefault();
    // console.log('Updating variant...', event, button);
    const newVariantId = button.dataset.variantId;
    const newVariantPrice = button.dataset.variantPrice;
    const isAvailable = button.dataset.available === "true";
    const optionIndex = button.dataset.optionIndex;
    const newValue = button.dataset.value;

    if (this.debug)
      console.log("Handling variant update:", {
        newVariantId,
        newVariantPrice,
        isAvailable,
        optionIndex,
        newValue,
      });

    // Track user's size preference when they explicitly select a size
    if (this._isOptionSize(optionIndex, newValue)) {
      this.preferredSize = newValue;
      this.hasUserSelectedSize = true;
      if (this.debug)
        console.log("User selected size preference:", this.preferredSize);
    }

    if (!newVariantId || !optionIndex || !newValue) {
      console.error("Missing required button dataset attributes:", {
        newVariantId,
        optionIndex,
        newValue,
      });
      return;
    }

    const addToCartForm = document.querySelector(
      ".product-info .shopify-product-form"
    );
    const input = addToCartForm?.querySelector('input[name="id"]');
    const soldOutElement = document.querySelector(
      ".product-info sold-out-badge"
    );

    if (!addToCartForm || !input) {
      console.error("Add to cart form or input not found:", {
        addToCartForm: !!addToCartForm,
        input: !!input,
      });
      return;
    }

    this.currentOptions[`option${optionIndex}`] = newValue;
    this.currentVariantId = newVariantId;
    input.value = newVariantId;
    this.updateVariantClass();
    this._updateURLWithVariant(newVariantId);

    if (this.debug) {
      console.log("Updated currentOptions:", this.currentOptions);
      console.log("Updated currentVariantId:", this.currentVariantId);
    }

    // Notify AMP Back in Stock app about variant change
    this._notifyAmpBackInStock({
      id: newVariantId,
      available: isAvailable,
      price: newVariantPrice,
    });

    // CRITICAL: Dispatch variant:change event for other systems (custom.js, etc.)
    // This ensures all parts of the system stay in sync
    document.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          variant: {
            id: newVariantId,
            available: isAvailable,
            price: newVariantPrice,
          },
          formId: addToCartForm?.id,
        },
      })
    );

    const stockMessages = [
      ...this.parentComponent.querySelectorAll("product-stock-message"),
      ...(this.parentComponent
        .closest("quick-view-modal")
        ?.querySelectorAll("product-stock-message") || []),
    ];
    stockMessages.forEach((stockMessage) => {
      stockMessage.updateVariantId(newVariantId);
    });

    // Fetch real-time variant availability to ensure consistency
    this._fetchVariantAvailability(newVariantId)
      .then((realTimeAvailable) => {
        if (this.debug) {
          console.log("[ProductGrouping] Real-time availability fetched:", {
            variantId: newVariantId,
            realTimeAvailable,
            cachedAvailable: isAvailable,
          });
        }
        // Re-query buttons fresh inside callback to avoid stale references
        // Don't filter by type="submit" since it may have been changed by third-party scripts
        const buyButtonsEl = addToCartForm?.querySelector("buy-buttons");
        const allButtons = buyButtonsEl
          ? Array.from(buyButtonsEl.querySelectorAll("button"))
          : [];

        if (this.debug) {
          console.log("[ProductGrouping] Found buttons:", {
            totalButtons: allButtons.length,
            buttons: allButtons.map((btn) => ({
              hasBisTrigger: btn.hasAttribute("data-bis-trigger"),
              name: btn.name,
              type: btn.type,
              id: btn.id,
              display: btn.style.display,
            })),
          });
        }

        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = allButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger")
        );

        const addToCartButtonText = addToCartButton?.querySelector("div");

        // CRITICAL: Always ensure we have both buttons before proceeding
        if (!addToCartButton || !notifyMeBtn) {
          console.warn(
            "[ProductGrouping] Button elements not found, retrying...",
            { addToCartButton: !!addToCartButton, notifyMeBtn: !!notifyMeBtn }
          );
          // Retry once after a short delay
          setTimeout(() => {
            const retryBuyButtonsEl =
              addToCartForm?.querySelector("buy-buttons");
            const retryAllButtons = retryBuyButtonsEl
              ? Array.from(retryBuyButtonsEl.querySelectorAll("button"))
              : [];
            const retryNotifyMeBtn = retryAllButtons.find((btn) =>
              btn.hasAttribute("data-bis-trigger")
            );
            const retryAddToCartButton = retryAllButtons.find(
              (btn) => !btn.hasAttribute("data-bis-trigger")
            );
            const retryAddToCartButtonText =
              retryAddToCartButton?.querySelector("div");

            if (retryAddToCartButton && retryNotifyMeBtn) {
              this._updateButtonVisibility(
                retryAddToCartButton,
                retryNotifyMeBtn,
                retryAddToCartButtonText,
                soldOutElement,
                realTimeAvailable
              );
            }
          }, 50);
          return;
        }

        // Update button visibility using helper method to ensure consistency
        this._updateButtonVisibility(
          addToCartButton,
          notifyMeBtn,
          addToCartButtonText,
          soldOutElement,
          realTimeAvailable
        );

        this.updateQuickAddCTA(
          realTimeAvailable,
          newVariantId,
          newVariantPrice
        );
        setTimeout(() => {
          document
            .querySelectorAll('product-stock-message[data-context="pdp"]')
            .forEach((el) => {
              el.hasFetched = false; // Force it to re-fetch
              el.updateVariantId(newVariantId);
            });
        }, 100);
      })
      .catch((error) => {
        console.error(
          "[ProductGrouping] Failed to fetch real-time variant availability, falling back to cached data:",
          error
        );

        // Fallback to original cached logic if API fails
        // Re-query buttons to ensure we have fresh references
        const fallbackBuyButtonsEl =
          addToCartForm?.querySelector("buy-buttons");
        const fallbackAllButtons = fallbackBuyButtonsEl
          ? Array.from(fallbackBuyButtonsEl.querySelectorAll("button"))
          : [];
        const fallbackNotifyMeBtn = fallbackAllButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const fallbackAddToCartButton = fallbackAllButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger")
        );
        const fallbackAddToCartButtonText =
          fallbackAddToCartButton?.querySelector("div");

        if (fallbackAddToCartButton && fallbackNotifyMeBtn) {
          this._updateButtonVisibility(
            fallbackAddToCartButton,
            fallbackNotifyMeBtn,
            fallbackAddToCartButtonText,
            soldOutElement,
            isAvailable
          );
        }

        this.updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice);
      });
  }

  /**
   * Central method to update button visibility - ensures consistent behavior
   * CRITICAL: This is the single source of truth for button state management
   */
  _updateButtonVisibility(addToCartButton, notifyMeBtn, addToCartButtonText, soldOutElement, isAvailable) {
    if (!addToCartButton || !notifyMeBtn) {
      console.error('[ProductGrouping] Cannot update button visibility - buttons not found');
      return;
    }

    if (this.debug) {
      console.log('[ProductGrouping] _updateButtonVisibility called', {
        isAvailable,
        hasSoldOutElement: !!soldOutElement,
        addToCartButtonDisplay: addToCartButton.style.display,
        notifyMeBtnDisplay: notifyMeBtn.style.display
      });
    }

    if (isAvailable) {
      // Variant is available - ALWAYS show Add to Bag button
      // Only update text if it doesn't already contain price information
      if (
        addToCartButtonText &&
        !addToCartButtonText.textContent.includes("$")
      ) {
        addToCartButtonText.textContent = "Add to Bag";
      }

      // CRITICAL: Ensure button is always visible and enabled for available variants
      // Use !important to override any CSS interference
      addToCartButton.removeAttribute("disabled");
      addToCartButton.classList.remove("button--subdued");
      addToCartButton.setAttribute("type", "submit");
      addToCartButton.style.setProperty("display", "block", "important");
      addToCartButton.style.setProperty("visibility", "visible", "important");
      addToCartButton.style.setProperty("opacity", "1", "important");
      addToCartButton.style.removeProperty("pointer-events"); // Re-enable interaction

      // Hide waitlist button with !important
      notifyMeBtn.style.setProperty("display", "none", "important");
      notifyMeBtn.style.setProperty("visibility", "hidden", "important");
      notifyMeBtn.style.setProperty("opacity", "0", "important");

      // Hide sold out badge
      if (soldOutElement) {
        soldOutElement.classList.add("is-hidden");
      }

      if (this.debug) {
        console.log(
          "[ProductGrouping] Button visibility updated: Add to Bag shown"
        );
      }
    } else {
      // Variant is sold out - show Join Waitlist button
      this.showJoinWaitListButton(
        addToCartButton,
        notifyMeBtn,
        addToCartButtonText,
        soldOutElement
      );

      if (this.debug) {
        console.log(
          "[ProductGrouping] Button visibility updated: Join Waitlist shown"
        );
      }
    }
  }

  updateQuickAddCTA(isAvailable, newVariantId, newVariantPrice) {
    console.log("updateQuickAddCTA called:", {
      isAvailable,
      newVariantId,
      newVariantPrice,
    });
    document
      .querySelectorAll("product-quick-add, buy-buttons")
      .forEach((quickAdd) => {
        const $submit = quickAdd.querySelector(".buy-buttons__submit");
        // console.log('🚀 -> $submit:', $submit)
        const input = quickAdd.querySelector('input[name="id"]');
        // console.log('🚀 -> input:', input)
        if (input) input.value = newVariantId;

        if (isAvailable && $submit) {
          const isPreorder = document
            .querySelector("#main-product-info")
            ?.classList.contains("preorder");
          const newLabel = isPreorder
            ? `PREORDER - ${newVariantPrice}`
            : `Add to Bag - ${newVariantPrice}`;
          console.log("Setting new label:", newLabel);

          // Handle both structures: buttons with <div> wrapper (quick-add) and plain text buttons (PDP)
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            console.log("Found div wrapper, updating div.textContent");
            $labelElement.textContent = newLabel;
          } else {
            console.log("No div wrapper, updating button.textContent directly");
            $submit.textContent = newLabel;
          }
          console.log("Label updated successfully");
          $submit.setAttribute("type", "submit");
          $submit.removeEventListener("click", this._scrollToBuyButtons);
        } else if ($submit) {
          const $labelElement = $submit.querySelector("div");
          if ($labelElement) {
            $labelElement.textContent = "Join Waitlist";
          } else {
            $submit.textContent = "Join Waitlist";
          }
          $submit.setAttribute("type", "button");
          $submit.addEventListener("click", this._scrollToBuyButtons);
        }
      });
  }

  _setupWaitlistScroll() {
    const $submit = document.querySelectorAll("product-quick-add .buy-buttons__submit");
    $submit.forEach((submit) => {
      submit.addEventListener("click", this._scrollToBuyButtons);
    });
  }

  _scrollToBuyButtons(event) {
    event.preventDefault();
    const scrollToElement = document.querySelector('.product-info__buy-buttons');
    if (scrollToElement) {
      scrollToElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  updateVariantClass() {
    Object.values(this.currentOptions).forEach((data, index) => {
      // console.log(' this.currentOptions ', index, this.currentOptions)

      let optionIndex = index;
      if (this.currentOptions.option2.length) {
        optionIndex = optionIndex + 1
      }
      if (this.currentOptions.option3.length) {
        optionIndex = optionIndex + 1
      }
      // console.log('🚀 -> optionIndex:', optionIndex)
      const row = this.querySelector(`.product-grouping__options[data-options-row="${optionIndex}"]`);
      // console.log('🚀 -> row:', row)

      if (optionIndex === 1) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
        });
      }
      if (optionIndex === 2) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option3 === this.currentOptions.option3;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
      if (optionIndex === 3) {
        row?.querySelectorAll('button[js-update-variant]').forEach(button => {
          const isVisible = button.dataset.option2 === this.currentOptions.option2;
          const isActive = button.dataset.value === this.currentOptions[`option${optionIndex}`];
          button.classList.toggle('is-active', isActive);
          button.classList.toggle('is-hidden', !isVisible);
        });
      }
    });
  }

  showJoinWaitListButton(addToCartButton, notifyMeBtn, addToCartButtonText, soldOutElement) {
    if (!addToCartButton || !notifyMeBtn) {
      console.warn(
        "[ProductGrouping] showJoinWaitListButton called with missing buttons"
      );
      return;
    }

    // CRITICAL: Completely hide and disable Add to Cart button
    // We need to be aggressive here to override any CSS rules
    addToCartButton.setAttribute("disabled", "disabled");
    addToCartButton.classList.add("button--subdued");
    addToCartButton.setAttribute("type", "button"); // Remove submit capability
    addToCartButton.style.setProperty("display", "none", "important");
    addToCartButton.style.setProperty("visibility", "hidden", "important");
    addToCartButton.style.setProperty("opacity", "0", "important");
    addToCartButton.style.setProperty("pointer-events", "none", "important");

    if (addToCartButtonText) {
      addToCartButtonText.textContent = "Sold Out";
    }

    // Show waitlist button with !important to override any interference
    notifyMeBtn.style.setProperty("display", "block", "important");
    notifyMeBtn.style.setProperty("visibility", "visible", "important");
    notifyMeBtn.style.setProperty("opacity", "1", "important");

    // Show sold out badge
    if (soldOutElement) {
      soldOutElement.classList.remove("is-hidden");
    }

    if (this.debug) {
      console.log("[ProductGrouping] Waitlist button shown for OOS variant");
    }
  }

  async _fetchVariantStock(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name');
      return 0;
    }

    const query = `
      query GetVariantStock($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            quantityAvailable
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error: ${response.status}`, await response.text());
        return 0;
      }

      const result = await response.json();
      const qty = result?.data?.node?.quantityAvailable ?? 0;
      return qty;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching stock quantity:', error);
      return 0;
    }
  }

  async _fetchVariantAvailability(variantId) {
    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) {
      console.error('[ProductGrouping] Missing Shopify token or store name for availability check');
      return false;
    }

    const query = `
      query GetVariantAvailability($variantId: ID!) {
        node(id: $variantId) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;

    const variables = { variantId: `gid://shopify/ProductVariant/${variantId}` };

    try {
      const response = await fetch(`https://${store}.myshopify.com/api/2025-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': accessToken,
          'Accept': 'application/json',
          'Accept-Language': locale,
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.error(`[ProductGrouping] Shopify API error for availability: ${response.status}`, await response.text());
        return false;
      }

      const result = await response.json();
      const available = result?.data?.node?.availableForSale ?? false;
      
      if (this.debug) {
        console.log('[ProductGrouping] API Response for variant', variantId, ':', {
          fullResult: result,
          availableForSale: available,
          node: result?.data?.node
        });
      }
      
      return available;
    } catch (error) {
      console.error('[ProductGrouping] Error fetching real-time variant availability:', error);
      return false;
    }
  }

  _notifyAmpBackInStock(variant) {
    try {
      // Method 1: Update the variant input that the app might be watching
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput && variant.id) {
        variantInput.value = variant.id;
        // Trigger change event to notify any listeners
        variantInput.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Method 2: Update data attributes that the app reads when the modal opens
      const bisButtons = document.querySelectorAll("[data-bis-trigger]");
      bisButtons.forEach((button) => {
        button.setAttribute("data-variant-id", variant.id);
        button.setAttribute(
          "data-product-id",
          variant.product_id || window.meta?.product?.id || ""
        );
      });
    } catch (error) {
      console.warn(
        "[ProductGrouping] Error notifying AMP Back in Stock app:",
        error
      );
    }
  }

  async _handleOptionChange(event, option) {
    event.preventDefault();
    if (option.classList.contains('is-disabled')) return;

    const newProduct = option.dataset.productHandle;
    const loadedIds = this.dataProductGroup?.loaded_ids?.replace(/"/g, '') || false;

    if (newProduct && newProduct !== this.currentProductHandle) {
      try {
        this.parentComponent.classList.add("is-loading");

        // Capture current option2 and option3 values to preserve across color changes
        const currentVariant = this._getCurrentVariant();
        const targetOption2 = currentVariant?.option2;
        const targetOption3 = currentVariant?.option3;

        // Performance: Check cache first to avoid redundant fetches
        let productData;
        if (this.productCache.has(newProduct)) {
          productData = this.productCache.get(newProduct);
          if (this.debug)
            console.log(
              "[ProductGrouping] Using cached product data for:",
              newProduct
            );
        } else {
          // First, get the new product data to find matching variant
          let previewUrl = new URL(
            window.location.origin + `/products/${newProduct}.json`
          );
          const productResponse = await fetch(previewUrl);

          // Validate response before parsing
          if (!productResponse.ok) {
            console.error(
              `[ProductGrouping] Failed to fetch product: ${productResponse.status}`
            );
            throw new Error(`Product fetch failed: ${productResponse.status}`);
          }

          const contentType = productResponse.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            console.error(
              `[ProductGrouping] Invalid content-type: ${contentType}`
            );
            throw new Error("Product response is not JSON");
          }

          productData = await productResponse.json();

          // Cache for future clicks
          this.productCache.set(newProduct, productData);
          if (this.debug)
            console.log(
              "[ProductGrouping] Cached product data for:",
              newProduct
            );
        }

        // Find variant with matching option2/option3 in the new product
        let targetVariantId = null;
        if (
          productData.product &&
          productData.product.variants &&
          (targetOption2 || targetOption3)
        ) {
          targetVariantId = this._findMatchingVariant(
            productData.product.variants,
            targetOption2,
            targetOption3
          );
        }

        let url = new URL(window.location.origin + `/products/${newProduct}`);
        let params = new URLSearchParams();
        params.set("view", "product-info-ajax");
        params.set("section_id", this.sectionId);
        params.set("no-fade-in", "true");
        if (loadedIds) {
          params.set("product-group-ids", loadedIds);
        }

        // If we found a matching variant, include it in the request so the new product renders with correct size
        if (targetVariantId) {
          params.set("variant", targetVariantId);
          if (this.debug)
            console.log("Including variant in AJAX request:", targetVariantId);
        }

        url.search = params.toString();
        const decodedURL = url.toString().replace(/%2B/g, "+");
        if (this.debug) console.log("decodedURL:", decodedURL);

        // Performance: Cache HTML to avoid redundant fetches
        const cacheKey = `${newProduct}-${targetVariantId || "default"}`;
        let text;
        if (this.htmlCache.has(cacheKey)) {
          text = this.htmlCache.get(cacheKey);
          if (this.debug)
            console.log("[ProductGrouping] Using cached HTML for:", cacheKey);
        } else {
          const response = await fetch(decodedURL);
          text = await response.text();

          // Cache for future clicks
          this.htmlCache.set(cacheKey, text);
          if (this.debug)
            console.log("[ProductGrouping] Cached HTML for:", cacheKey);
        }

        const parser = new DOMParser();
        const newDocument = parser.parseFromString(text, "text/html");

        const newMainProduct = newDocument.querySelector("#main-product-info");
        const currentMainProduct = this.isQuickView
          ? document.querySelector("quick-view-modal .quick-add-modal")
          : document.querySelector("#main-product-info");

        this.firstVariantId =
          newMainProduct?.getAttribute("data-first-variant");
        this.productId = newMainProduct?.getAttribute("data-product-id");
        this.currentProductHandle = newProduct;
        this.currentProductId = this.productId;

        // Use the target variant if we found one, otherwise find first available variant
        if (targetVariantId) {
          this.currentVariantId = targetVariantId;
        } else {
          const firstAvailableVar = productData.product?.variants?.find(
            (v) => v.available
          );
          this.currentVariantId =
            firstAvailableVar?.id.toString() || this.firstVariantId;
        }

        if (newMainProduct && currentMainProduct) {
          this.isQuickView
            ? (currentMainProduct.innerHTML = newMainProduct.innerHTML)
            : currentMainProduct.replaceWith(newMainProduct);
        }

        // Update the variant input field with the correct variant ID
        const variantInput = document.querySelector(
          '.product-info input[name="id"]'
        );
        if (variantInput && this.currentVariantId) {
          variantInput.value = this.currentVariantId;
        }

        // Re-bind variant buttons
        this._rebindVariantButtons();

        // Update product grouping data from the new HTML
        this.dataProductGroup = JSON.parse(
          this.querySelector("script[data-product-group]")?.textContent || "{}"
        );

        // Rebuild productGroupingData to include the new product's variant data
        const scriptTagsData = this.querySelectorAll(
          "script[js-product-group-data]"
        );
        this.productGroupingData = Array.from(scriptTagsData).reduce(
          (acc, script) => {
            const data = JSON.parse(script.textContent || "{}");
            const key = script.getAttribute("data-product-id");
            if (!acc[key]) {
              acc[key] = { tag_options: [], variants: [] };
            }
            const existingData = acc[key];
            existingData.tag_options.push(...data.tag_options);
            const uniqueVariants = data.variants.filter(
              (newVariant) =>
                !existingData.variants.some(
                  (existingVariant) => existingVariant.id === newVariant.id
                )
            );
            existingData.variants.push(...uniqueVariants);
            return acc;
          },
          this.productGroupingData || {}
        );

        // Also add the new product data directly from our API fetch
        if (productData.product && this.currentProductId) {
          this.productGroupingData[this.currentProductId] = {
            tag_options: [],
            variants: productData.product.variants || [],
          };
        }

        // Update currentOptions to reflect the selected variant using data from API
        const selectedVariant = productData.product?.variants?.find(
          (v) => v.id.toString() === this.currentVariantId.toString()
        );
        if (selectedVariant) {
          this.currentOptions.option2 =
            selectedVariant.option2 || this.currentOptions.option2;
          this.currentOptions.option3 =
            selectedVariant.option3 || this.currentOptions.option3;
        }

        // Update the visual state of size/option buttons to reflect the selected variant
        this.updateVariantClass();

        // CRITICAL: Wait for DOM to fully render before querying buttons
        // Use requestAnimationFrame to ensure the replaced HTML is painted
        requestAnimationFrame(() => {
          // Reset button state for the new product and check availability
          const newAddToCartForm = document.querySelector(
            ".product-info .shopify-product-form"
          );
          const newSoldOutElement = document.querySelector(
            ".product-info sold-out-badge"
          );

          // CRITICAL: Fetch real-time availability for the selected variant after color change
          if (this.currentVariantId) {
            this._fetchVariantAvailability(this.currentVariantId)
              .then((realTimeAvailable) => {
                if (this.debug) {
                  console.log(
                    "[ProductGrouping] Color change - real-time availability:",
                    {
                      variantId: this.currentVariantId,
                      realTimeAvailable,
                    }
                  );
                }

                // Re-query buttons after DOM update
                const buyButtonsEl =
                  newAddToCartForm?.querySelector("buy-buttons");
                const allButtons = buyButtonsEl
                  ? Array.from(buyButtonsEl.querySelectorAll("button"))
                  : [];

                const newNotifyMeBtn = allButtons.find((btn) =>
                  btn.hasAttribute("data-bis-trigger")
                );
                const newAddToCartButton = allButtons.find(
                  (btn) => !btn.hasAttribute("data-bis-trigger")
                );
                // Handle both button structures: with div wrapper (quick-add) and without (PDP)
                const newAddToCartButtonText =
                  newAddToCartButton?.querySelector("div") ||
                  newAddToCartButton;

                if (newAddToCartButton && newNotifyMeBtn) {
                  this._updateButtonVisibility(
                    newAddToCartButton,
                    newNotifyMeBtn,
                    newAddToCartButtonText,
                    newSoldOutElement,
                    realTimeAvailable
                  );

                  // CRITICAL: Notify AMP Back in Stock app about the new variant after color change
                  // This ensures the BIS modal shows the correct size options for the newly selected color
                  this._notifyAmpBackInStock({
                    id: this.currentVariantId,
                    available: realTimeAvailable,
                  });

                  // Dispatch variant:change event for other systems
                  document.dispatchEvent(
                    new CustomEvent("variant:change", {
                      bubbles: true,
                      detail: {
                        variant: {
                          id: this.currentVariantId,
                          available: realTimeAvailable,
                        },
                        formId: newAddToCartForm?.id,
                      },
                    })
                  );
                } else {
                  console.warn(
                    "[ProductGrouping] Buttons not found after color change, retrying...",
                    {
                      hasAddToCart: !!newAddToCartButton,
                      hasWaitlist: !!newNotifyMeBtn,
                      totalButtons: allButtons.length,
                    }
                  );
                }
              })
              .catch((error) => {
                console.error(
                  "[ProductGrouping] Failed to fetch availability after color change:",
                  error
                );
              });
          }
        });

        const scripts = Array.from(
          newMainProduct?.querySelectorAll("script") || []
        ).filter((script) => !script.hasAttribute("data-product-group"));
        scripts.forEach((oldScript) => {
          const newScript = document.createElement("script");
          newScript.textContent = oldScript.textContent;
          document.body.appendChild(newScript);
          document.body.removeChild(newScript);
        });

        window.dispatchEvent(new Event("productGroupingInjected"));

        if (!this.isQuickView) {
          params.delete("view");
          params.delete("section_id");
          params.delete("no-fade-in");
          if (loadedIds) {
            params.delete("product-group-ids");
          }
          url.search = params.toString();
          window.history.replaceState({}, "", url.toString());

          const optionColor = document.querySelector(
            `button[data-product-handle="${newProduct}"]`
          );
          const currentQuickAdd = document.querySelector("#quick-add--sticky");
          const newQuickAdd = newDocument.querySelector("#quick-add--sticky");
          if (currentQuickAdd && newQuickAdd) {
            currentQuickAdd.replaceWith(newQuickAdd);
          }

          setTimeout(() => {
            if (!this.isQuickView) {
              window.yotpoWidgetsContainer?.initWidgets();
            }
            if (optionColor) {
              optionColor.focus();
            }
            const isMobile = window.matchMedia("(max-width: 768px)").matches;
            if (isMobile) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }, 300);
        }

        this.currentProductHandle = newProduct;
      } catch (error) {
        console.error(
          "[ProductGrouping] Error fetching new product data:",
          error
        );

        // User-friendly fallback: reload the page with the correct product
        if (this.debug) {
          console.log(
            "[ProductGrouping] Attempting full page navigation to:",
            newProduct
          );
        }
        // Only navigate if we have a valid product handle
        if (newProduct && typeof newProduct === "string") {
          window.location.href = `/products/${newProduct}`;
        }
      } finally {
        this.parentComponent.classList.remove('is-loading');
      }
    }
  }

  _setupSizeGuide() {
    this.sizeGuideButton = document.querySelector(".open-size-guide");
    this.sizeGuideModal = document.querySelector(".size-guide-modal");
    this.closeButton = document.querySelector(".modal-close");
    this.modalContent = document.querySelector(".modal-content");

    if (this.sizeGuideButton) {
      this.sizeGuideButton.addEventListener("click", () => this.openSizeGuideModal());
    }

    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.closeSizeGuideModal());
    }

    if (this.sizeGuideModal) {
      this.sizeGuideModal.addEventListener("click", (event) => {
        if (event.target === this.sizeGuideModal) {
          this.closeSizeGuideModal();
        }
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.sizeGuideModal?.classList.contains("active")) {
        this.closeSizeGuideModal();
      }
    });

    this.tabButtons = document.querySelectorAll(".size-guide-tab-buttons .tab-button");
    this.tabPanels = document.querySelectorAll(".size-guide-tab-content .tab-panel");

    this.tabButtons.forEach((button) => {
      button.addEventListener("click", (event) => this.switchTab(event));
    });

    window.addEventListener("productGroupingInjected", () => this._updateSizeGuide());
  }

  switchTab(event) {
    const selectedTab = event.target.getAttribute("data-tab");
    this.tabButtons.forEach((btn) => btn.classList.remove("active"));
    this.tabPanels.forEach((panel) => panel.classList.remove("active"));
    event.target.classList.add("active");
    document.getElementById(selectedTab)?.classList.add("active");
  }

  openSizeGuideModal() {
    this.sizeGuideModal?.classList.add("active");
    document.body.classList.add('modal-open');
    this._generateSizeChartTabs();
  }

  closeSizeGuideModal() {
    this.sizeGuideModal?.classList.remove('active');
    document.body.classList.remove('modal-open');
  }

  _generateSizeChartTabs() {
    const sizeChartContent = document.querySelector('#size-chart-content');
    if (!sizeChartContent) return;

    const tables = sizeChartContent.querySelectorAll("table");
    const paragraphs = sizeChartContent.querySelectorAll("p");

    if (tables.length === 0) return;

    let subTabsHTML = `<div class="size-guide-sub-tabs" role="tablist">`;
    let subContentHTML = `<div class="size-guide-sub-content">`;

    tables.forEach((table, index) => {
      let label = `Size Chart ${index + 1}`;
      for (let i = paragraphs.length - 1; i >= 0; i--) {
        if (paragraphs[i].nextElementSibling === table && paragraphs[i].textContent.trim() !== "") {
          label = paragraphs[i].textContent.trim();
          break;
        }
      }
      subTabsHTML += `<button class="sub-tab-button ${index === 0 ? 'active' : ''}" data-tab="sub-tab-${index}" role="tab" aria-controls="sub-tab-${index}" aria-selected="${index === 0 ? 'true' : 'false'}">${label}</button>`;
      subContentHTML += `<div class="sub-tab-panel ${index === 0 ? 'active' : ''}" id="sub-tab-${index}" role="tabpanel">${table.outerHTML}</div>`;
    });

    subTabsHTML += `</div>`;
    subContentHTML += `</div>`;

    sizeChartContent.innerHTML = subTabsHTML + subContentHTML;

    document.querySelectorAll(".sub-tab-button").forEach(button => {
      button.addEventListener("click", function() {
        const tab = this.getAttribute("data-tab");
        document.querySelectorAll(".sub-tab-button").forEach(btn => btn.classList.remove("active"));
        document.querySelectorAll(".sub-tab-panel").forEach(panel => panel.classList.remove("active"));
        this.classList.add("active");
        document.getElementById(tab)?.classList.add("active");
      });
    });
  }

  _updateSizeGuide() {
    const productTags = this.getAttribute("data-product-tags") || "";
    const sizeGuideHandle = this._extractSizeGuideTag(productTags);

    if (sizeGuideHandle) {
      fetch(`/pages/${sizeGuideHandle}`)
        .then(response => response.text())
        .then(sizeChart => {
          document.querySelector("#size-chart-content").innerHTML = sizeChart;
        })
        .catch(error => console.error("Error loading size guide:", error));
    }
  }

  _extractSizeGuideTag(tags) {
    const match = tags.split(",").find(tag => tag.includes("size-guide:"));
    return match ? match.split(":")[1] : "";
  }

  /**
   * Get the currently selected size from active size buttons
   */
  _getCurrentSelectedSize() {
    // Look for active size buttons in the product grouping component
    const activeSizeButton = this.querySelector('.product-grouping__options.is-size .product-option.is-active');
    if (activeSizeButton) {
      const sizeText = activeSizeButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active size button:', sizeText);
      return sizeText;
    }

    // Fallback: check variant picker size buttons  
    const activeVariantButton = this.querySelector('.variant-picker__option-values .product-option.is-active');
    if (activeVariantButton) {
      const sizeText = activeVariantButton.querySelector('span')?.textContent?.trim();
      if (this.debug) console.log('Found active variant button:', sizeText);
      return sizeText;
    }

    // Last resort: get from current variant data
    const currentVariant = this._getCurrentVariant();
    if (currentVariant) {
      // Size could be in option2 or option3 depending on product structure
      const possibleSize = currentVariant.option2 || currentVariant.option3;
      if (possibleSize && possibleSize !== 'Default Title') {
        if (this.debug) console.log('Found size from current variant:', possibleSize);
        return possibleSize;
      }
    }

    if (this.debug) console.log('No current size found');
    return null;
  }

  /**
   * Get current variant data from the variant picker or product grouping
   */
  _getCurrentVariant() {
    try {
      // First, try variant-picker (for standard products)
      const variantScript = document.querySelector('variant-picker script[data-variant]');
      if (variantScript) {
        const variant = JSON.parse(variantScript.textContent || '{}');
        if (variant && variant.id) {
          if (this.debug) console.log('Found variant from variant-picker:', variant);
          return variant;
        }
      }

      // Fallback: For product-grouping, reconstruct from current state
      if (this.currentVariantId && this.productGroupingData && this.currentProductId) {
        const productData = this.productGroupingData[this.currentProductId];
        if (productData && productData.variants) {
          const currentVariant = productData.variants.find(
            v => v.id.toString() === this.currentVariantId.toString()
          );
          if (currentVariant) {
            if (this.debug) console.log('Found variant from product-grouping data:', currentVariant);
            return currentVariant;
          }
        }
      }

      // Last resort: Use tracked options to build a partial variant object
      if (this.currentOptions.option2 || this.currentOptions.option3) {
        const partialVariant = {
          id: this.currentVariantId,
          option2: this.currentOptions.option2,
          option3: this.currentOptions.option3
        };
        if (this.debug) console.log('Built variant from currentOptions:', partialVariant);
        return partialVariant;
      }
    } catch (error) {
      if (this.debug) console.warn('Could not parse current variant:', error);
    }

    if (this.debug) console.warn('No current variant found from any source');
    return null;
  }

  /**
   * Find a variant that matches the target option2 and/or option3 values
   * Works for both regular products (single size) and pants (waist + inseam)
   */
  _findMatchingVariant(variants, targetOption2, targetOption3) {
    if (!variants || (!targetOption2 && !targetOption3)) {
      if (this.debug) console.log('_findMatchingVariant: No variants or target options provided');
      return null;
    }

    // Normalize "Default Title" to null for easier comparison
    const normalizedOption2 = targetOption2 === 'Default Title' ? null : targetOption2;
    const normalizedOption3 = targetOption3 === 'Default Title' ? null : targetOption3;

    if (this.debug) {
      console.log('_findMatchingVariant searching for:', {
        option2: normalizedOption2,
        option3: normalizedOption3,
        totalVariants: variants.length
      });
      // Log a sample of variants to see their structure
      console.log('Sample variants:', variants.slice(0, 3).map(v => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3
      })));
    }

    try {
      for (const variant of variants) {
        // Match both option2 and option3 if both are meaningful values
        if (normalizedOption2 && normalizedOption3) {
          // Normalize option values by removing inch symbols and whitespace for comparison
          // This handles cases where one product uses '32' and another uses '32"'
          const normalizeValue = (val) => val ? val.toString().replace(/["'"\s]/g, '') : '';
          
          const variantOption2Normalized = normalizeValue(variant.option2);
          const targetOption2Normalized = normalizeValue(normalizedOption2);
          const variantOption3Normalized = normalizeValue(variant.option3);
          const targetOption3Normalized = normalizeValue(normalizedOption3);
          
          const option2Match = variantOption2Normalized === targetOption2Normalized;
          const option3Match = variantOption3Normalized === targetOption3Normalized;
          
          if (this.debug && (option2Match || option3Match)) {
            console.log('Checking variant:', variant.id, {
              variantOption2: variant.option2,
              variantOption2Normalized,
              targetOption2: normalizedOption2,
              targetOption2Normalized,
              option2Match,
              variantOption3: variant.option3,
              variantOption3Normalized,
              targetOption3: normalizedOption3,
              targetOption3Normalized,
              option3Match
            });
          }
          
          if (option2Match && option3Match) {
            if (this.debug) console.log('✓ Found exact match (option2 + option3):', variant.id, variant);
            return variant.id.toString();
          }
        }
        // Match just option2 if option3 is not meaningful
        else if (normalizedOption2 && !normalizedOption3) {
          if (variant.option2 === normalizedOption2) {
            if (this.debug) console.log('✓ Found option2 match:', variant.id, variant);
            return variant.id.toString();
          }
        }
      }
    } catch (error) {
      if (this.debug) console.warn('Error finding matching variant:', error);
    }

    if (this.debug) console.log('No matching variant found for the target options');
    return null;
  }

  /**
   * Check if an option represents a size selection
   */
  _isOptionSize(optionIndex, value) {
    // Look for size-related indicators
    const sizeIndicators = [
      "xs",
      "xsmall",
      "s",
      "small",
      "m",
      "medium",
      "l",
      "large",
      "xl",
      "xlarge",
      "xxl",
      "2xl",
      "3xl",
      "28",
      "29",
      "30",
      "31",
      "32",
      "33",
      "34",
      "35",
      "36",
      "38",
      "40",
      "42",
      "44",
      "46",
      "48",
      "50",
    ];

    const valueLower = value.toLowerCase().replace(/[^a-z0-9]/g, "");

    // Check if the value looks like a size
    const looksLikeSize = sizeIndicators.some((indicator) =>
      valueLower.includes(indicator)
    );

    // Also check if this option row is marked as size
    const optionRow = this.querySelector(`[data-options-row="${optionIndex}"]`);
    const isMarkedAsSize = optionRow?.classList.contains("is-size");

    return looksLikeSize || isMarkedAsSize;
  }

  /**
   * Get the size that should be preserved during color changes
   * Prioritizes user's preferred size if they've made a selection
   */
  _getSizeToPreserve() {
    // If user has explicitly selected a size, always try to preserve their preference
    if (this.hasUserSelectedSize && this.preferredSize) {
      return this.preferredSize;
    }

    // Otherwise fall back to current selection
    const currentSize = this._getCurrentSelectedSize();

    // If we found a current size and haven't captured a preference yet, capture it
    if (currentSize && !this.hasUserSelectedSize) {
      this.preferredSize = currentSize;
      this.hasUserSelectedSize = true;
    }
    
    return currentSize;
  }

  /**
   * Switch to a specific variant (programmatically)
   */
  _switchToVariant(variant) {
    if (!variant || !variant.button) {
      console.error('Invalid variant data for switching:', variant);
      return;
    }

    // Simulate a click on the variant button to trigger the normal flow
    const clickEvent = new Event('click', { bubbles: true, cancelable: true });
    variant.button.dispatchEvent(clickEvent);
  }

  /**
   * CRITICAL SAFETY NET: Monitor button state and prevent unwanted hiding
   * This acts as a final safeguard against any code that might hide the Add to Cart button
   * OPTIMIZED: Uses MutationObserver instead of polling for better performance
   */
  _setupButtonStateGuardian() {
    // Wait for DOM to be fully ready
    setTimeout(() => {
      const addToCartForm = document.querySelector(".product-info .shopify-product-form");
      if (!addToCartForm) return;

      const buyButtonsEl = addToCartForm.querySelector("buy-buttons");
      if (!buyButtonsEl) return;

      let isVerifying = false; // Prevent concurrent verifications

      const verifyButtonState = () => {
        // Skip if already verifying or no variant selected
        if (isVerifying || !this.currentVariantId) return;

        const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
        const notifyMeBtn = allButtons.find((btn) =>
          btn.hasAttribute("data-bis-trigger")
        );
        const addToCartButton = allButtons.find(
          (btn) => !btn.hasAttribute("data-bis-trigger") && btn.name === "add"
        );

        if (!addToCartButton || !notifyMeBtn) return;

        // Check for suspicious button states
        const isAddToCartHidden =
          addToCartButton.style.display === "none" ||
          addToCartButton.style.visibility === "hidden";
        const isWaitlistHidden = notifyMeBtn.style.display === "none";

        // If both are hidden, that's definitely wrong
        if (isAddToCartHidden && isWaitlistHidden) {
          if (this.debug) {
            console.warn(
              "[ProductGrouping] Both buttons hidden - correcting state"
            );
          }

          isVerifying = true;
          this._fetchVariantAvailability(this.currentVariantId)
            .then((isAvailable) => {
              const soldOutElement = document.querySelector(
                ".product-info sold-out-badge"
              );
              const buttonText = addToCartButton.querySelector("div");

              this._updateButtonVisibility(
                addToCartButton,
                notifyMeBtn,
                buttonText,
                soldOutElement,
                isAvailable
              );

              if (this.debug) {
                console.log(
                  `[ProductGrouping] Button state corrected to: ${
                    isAvailable ? "Add to Bag" : "Waitlist"
                  }`
                );
              }
            })
            .catch((error) => {
              console.error(
                "[ProductGrouping] Guardian failed to verify availability:",
                error
              );
            })
            .finally(() => {
              isVerifying = false;
            });
        }
      };

      // Use MutationObserver to watch for style changes (much more efficient than polling)
      const observer = new MutationObserver((mutations) => {
        // Only verify if style attributes changed
        const styleChanged = mutations.some(
          (m) => m.type === "attributes" && m.attributeName === "style"
        );
        if (styleChanged) {
          verifyButtonState();
        }
      });

      // Observe both buttons for style changes
      const allButtons = Array.from(buyButtonsEl.querySelectorAll("button"));
      allButtons.forEach((btn) => {
        observer.observe(btn, { attributes: true, attributeFilter: ["style"] });
      });

      // Initial check
      verifyButtonState();

      // Store cleanup function
      this._guardianCleanup = () => {
        observer.disconnect();
      };
    }, 100);
  }
}

customElements.define('product-grouping', ProductGrouping);